import React, { useMemo } from 'react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { Box } from '@mui/material';
import { CELL_COLORS, getCategoryColor, getBoothLabelColor } from './categoryPalette';
import { getLandmarkMeta } from './landmarkIcons';

const CELL_SIZE = 30;
const PADDING = 24;

// Matches a cell against the active search/category filter state. Returns a
// numeric "match score" so we can sort search results consistently and a
// boolean "passes" flag that controls dimming. Walkway/food/empty cells are
// always rendered at full opacity; only registered booths get dimmed.
const evaluateCell = (cell, normalizedQuery, selectedCategorySlugs) => {
  if (!cell) return { passes: true, isMatch: false };
  if (cell.cell_type !== 'booth' || !cell.is_registered) {
    return { passes: true, isMatch: false };
  }
  const hasCategoryFilter = selectedCategorySlugs && selectedCategorySlugs.size > 0;
  const categoryPasses = !hasCategoryFilter
    || (cell.category_slug && selectedCategorySlugs.has(cell.category_slug));

  if (!normalizedQuery) {
    return { passes: categoryPasses, isMatch: false };
  }
  const haystack = [cell.vendor_name, cell.category_name, cell.cell_name]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  const queryPasses = haystack.includes(normalizedQuery);
  return {
    passes: categoryPasses && queryPasses,
    isMatch: queryPasses && categoryPasses,
  };
};

// Compute a bounding rectangle in SVG units for every distinct section so we
// can drop a translucent label behind the booths. Memoized in the parent.
const computeSectionBounds = (cells) => {
  const bySection = new Map();
  for (const cell of cells) {
    if (!cell?.section) continue;
    const existing = bySection.get(cell.section);
    const x = cell.x_coordinate;
    const y = cell.y_coordinate;
    if (!existing) {
      bySection.set(cell.section, { minX: x, maxX: x, minY: y, maxY: y });
    } else {
      existing.minX = Math.min(existing.minX, x);
      existing.maxX = Math.max(existing.maxX, x);
      existing.minY = Math.min(existing.minY, y);
      existing.maxY = Math.max(existing.maxY, y);
    }
  }
  return Array.from(bySection.entries()).map(([section, b]) => ({
    section,
    cx: ((b.minX + b.maxX) / 2 - 0.5) * CELL_SIZE,
    cy: ((b.minY + b.maxY) / 2 - 0.5) * CELL_SIZE,
  }));
};

const MapCanvas = ({
  cells,
  landmarks,
  width,
  height,
  searchQuery,
  selectedCategorySlugs,
  selectedBoothId,
  highlightedBoothId,
  favoriteBoothIds,
  onBoothClick,
  transformRef,
}) => {
  const normalizedQuery = (searchQuery || '').trim().toLowerCase();
  const hasFilter = normalizedQuery.length > 0
    || (selectedCategorySlugs && selectedCategorySlugs.size > 0);

  const sectionBounds = useMemo(() => computeSectionBounds(cells), [cells]);

  const svgWidth = width * CELL_SIZE + PADDING * 2;
  const svgHeight = height * CELL_SIZE + PADDING * 2;

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        background: '#f5f6fa',
        touchAction: 'none',
        userSelect: 'none',
        '& .react-transform-wrapper': {
          width: '100%',
          height: '100%',
        },
        '& .react-transform-component': {
          width: '100%',
          height: '100%',
        },
      }}
    >
      <TransformWrapper
        ref={transformRef}
        initialScale={1}
        minScale={0.4}
        maxScale={4}
        wheel={{ step: 0.15 }}
        pinch={{ step: 5 }}
        doubleClick={{ disabled: false, mode: 'zoomIn', step: 0.7 }}
        limitToBounds={false}
        centerOnInit
      >
        <TransformComponent>
          <svg
            width={svgWidth}
            height={svgHeight}
            viewBox={`0 0 ${svgWidth} ${svgHeight}`}
            xmlns="http://www.w3.org/2000/svg"
            style={{ display: 'block' }}
          >
            <defs>
              <pattern
                id="walkwayPattern"
                patternUnits="userSpaceOnUse"
                width="6"
                height="6"
                patternTransform="rotate(45)"
              >
                <rect width="6" height="6" fill={CELL_COLORS.walkway} />
                <line x1="0" y1="0" x2="0" y2="6" stroke="#d8cdb6" strokeWidth="1" />
              </pattern>
              <filter id="boothShadow" x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow dx="0" dy="1" stdDeviation="0.6" floodOpacity="0.25" />
              </filter>
            </defs>

            <g transform={`translate(${PADDING}, ${PADDING})`}>
              {/* Section labels render behind cells so the colored tiles sit on top. */}
              {sectionBounds.map(({ section, cx, cy }) => (
                <text
                  key={`section-${section}`}
                  x={cx + CELL_SIZE / 2}
                  y={cy + CELL_SIZE / 2}
                  fill="#9e8c6b"
                  fillOpacity="0.35"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={CELL_SIZE * 1.6}
                  fontWeight="700"
                  style={{ pointerEvents: 'none' }}
                >
                  {section}
                </text>
              ))}

              {cells.map((cell) => {
                const x = (cell.x_coordinate - 1) * CELL_SIZE;
                const y = (cell.y_coordinate - 1) * CELL_SIZE;
                const { passes, isMatch } = evaluateCell(cell, normalizedQuery, selectedCategorySlugs);
                const isBooth = cell.cell_type === 'booth';
                const isRegistered = isBooth && cell.is_registered;
                const isSelected = selectedBoothId === cell.id;
                const isHighlighted = highlightedBoothId === cell.id;
                const isFavorite = favoriteBoothIds && favoriteBoothIds.has(cell.id);

                let fill;
                let stroke = 'transparent';
                let strokeWidth = 0;
                if (cell.cell_type === 'walkway') {
                  fill = 'url(#walkwayPattern)';
                } else if (cell.cell_type === 'food') {
                  fill = CELL_COLORS.food;
                } else if (cell.cell_type === 'empty') {
                  fill = CELL_COLORS.empty;
                } else if (isBooth && isRegistered) {
                  fill = getCategoryColor(cell.category_slug);
                } else if (isBooth) {
                  fill = CELL_COLORS.boothEmpty;
                  stroke = CELL_COLORS.boothBorder;
                  strokeWidth = 0.5;
                } else {
                  fill = CELL_COLORS.empty;
                }

                if (isSelected) {
                  stroke = CELL_COLORS.selected;
                  strokeWidth = 2;
                } else if (isHighlighted) {
                  stroke = CELL_COLORS.highlightRing;
                  strokeWidth = 2;
                }

                const showLabel = !!cell.cell_name && cell.cell_type !== 'walkway';
                const labelColor = isRegistered
                  ? getBoothLabelColor(cell.category_slug)
                  : CELL_COLORS.boothLabel;

                let opacity = 1;
                if (isBooth && hasFilter && !passes) opacity = 0.18;

                const interactive = isBooth && isRegistered;

                return (
                  <g
                    key={cell.id}
                    id={`booth-${cell.id}`}
                    transform={`translate(${x}, ${y})`}
                    style={{
                      cursor: interactive ? 'pointer' : 'default',
                      transition: 'opacity 0.25s ease',
                    }}
                    opacity={opacity}
                    onClick={interactive ? () => onBoothClick(cell) : undefined}
                  >
                    <rect
                      x={0.5}
                      y={0.5}
                      width={CELL_SIZE - 1}
                      height={CELL_SIZE - 1}
                      rx={isBooth ? 4 : 0}
                      ry={isBooth ? 4 : 0}
                      fill={fill}
                      stroke={stroke}
                      strokeWidth={strokeWidth}
                      filter={isRegistered ? 'url(#boothShadow)' : undefined}
                    />
                    {cell.is_corner && isBooth && (
                      <polygon
                        points={`${CELL_SIZE - 8},2 ${CELL_SIZE - 2},2 ${CELL_SIZE - 2},8`}
                        fill="#fff"
                        fillOpacity="0.7"
                      />
                    )}
                    {showLabel && (
                      <text
                        x={CELL_SIZE / 2}
                        y={CELL_SIZE / 2}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fontSize={10}
                        fontWeight="600"
                        fill={labelColor}
                        style={{ pointerEvents: 'none' }}
                      >
                        {cell.cell_name}
                      </text>
                    )}
                    {isFavorite && (
                      <text
                        x={3}
                        y={9}
                        fontSize={10}
                        fill="#fbc02d"
                        style={{ pointerEvents: 'none' }}
                      >
                        ★
                      </text>
                    )}
                    {isMatch && (
                      <rect
                        x={-1}
                        y={-1}
                        width={CELL_SIZE + 2}
                        height={CELL_SIZE + 2}
                        rx={5}
                        fill="none"
                        stroke={CELL_COLORS.highlightRing}
                        strokeWidth={1.5}
                      >
                        <animate
                          attributeName="stroke-opacity"
                          values="1;0.3;1"
                          dur="1.4s"
                          repeatCount="indefinite"
                        />
                      </rect>
                    )}
                  </g>
                );
              })}

              {/* Landmark pins. Rendered last so they sit on top of cells. */}
              {landmarks.map((landmark) => {
                const meta = getLandmarkMeta(landmark.kind);
                const Icon = meta.Icon;
                const cx = (landmark.x_coordinate - 0.5) * CELL_SIZE;
                const cy = (landmark.y_coordinate - 0.5) * CELL_SIZE;
                return (
                  <g
                    key={`landmark-${landmark.id}`}
                    transform={`translate(${cx}, ${cy})`}
                    style={{ pointerEvents: 'none' }}
                  >
                    <circle
                      r={13}
                      fill="#ffffff"
                      stroke={meta.color}
                      strokeWidth={2}
                      filter="url(#boothShadow)"
                    />
                    <circle r={11} fill={meta.color} fillOpacity="0.12" />
                    <Icon
                      x={-9}
                      y={-9}
                      width={18}
                      height={18}
                      htmlColor={meta.color}
                    />
                    {landmark.label && (
                      <text
                        x={0}
                        y={26}
                        textAnchor="middle"
                        fontSize={9}
                        fontWeight="600"
                        fill="#3e3a30"
                        stroke="#ffffff"
                        strokeWidth={3}
                        paintOrder="stroke"
                      >
                        {landmark.label}
                      </text>
                    )}
                  </g>
                );
              })}
            </g>
          </svg>
        </TransformComponent>
      </TransformWrapper>
    </Box>
  );
};

export default MapCanvas;
